/**
 * Dashboard "API & Agents" section — CRUD UI for API tokens (Phase 3,
 * 2026-05-20). Bearer tokens for programmatic eSIM purchases through
 * Claude/Cursor/Continue MCP server or any HTTP client.
 *
 * i18n: all user-facing strings via `t('dash.settings.apiTokens.X')`.
 * 5 locales: en, ru, es, pt, tr.
 *
 * Security model surfaced to user:
 *   - Plain text shown ONCE on creation. "Save now" inline warning.
 *   - Purchase scope OPT-IN (checkbox, default OFF) — read-only token by default.
 *   - First 7 days cool-off ($50 cap), can't override.
 *   - Daily/monthly spend limits configurable within (1-1000 / 1-10000).
 *   - Max 5 active tokens per user.
 *   - Each token displays prefix-hint + last-used IP+UA for spotting abuse.
 *   - One-click revoke (cannot be resurrected).
 */
import { escapeHtml } from '../../utils';
import { fmtDateTime } from '../../utils/format';
import { t } from '../../i18n';
import type { TranslationKey } from '../../i18n/types';

export interface ApiTokenView {
  id: string;
  name: string;
  prefix_hint: string;
  allow_purchase: boolean;
  daily_limit_usdt: number;
  monthly_limit_usdt: number;
  big_txn_threshold_usdt: number;
  spent_today_usdt: number;
  spent_month_usdt: number;
  cool_off_until: string;
  created_at: string;
  last_used_at: string | null;
  last_used_ip: string | null;
  last_used_ua: string | null;
  expires_at: string | null;
  revoked_at: string | null;
}

// Helper to keep call sites short. The cast is safe because we own
// both ends of the namespace ('dash.settings.apiTokens.X' all exist in
// en.json — see Phase 3 i18n additions). If a string is mistyped here
// the runtime t() falls back to the key itself + console.warn — still
// renderable, not a crash.
const tt = (suffix: string): string => t(`dash.settings.apiTokens.${suffix}` as TranslationKey);

/** Static HTML for the section. Token list is populated by JS after fetch. */
export function apiTokensSection(): string {
  return `
<section class="dash-settings__group" data-api-tokens-section>
  <h3>${escapeHtml(tt('groupTitle'))}</h3>
  <p class="dash-sub dash-callout">${tt('explainer')}</p>
  <p class="dash-sub">${escapeHtml(tt('intro'))}</p>
  <div style="display:flex; gap:8px; flex-wrap:wrap; margin-top:8px;">
    <a href="/api/v1/docs" target="_blank" rel="noopener" class="btn btn--ghost btn--sm" style="text-decoration:none;">${escapeHtml(tt('docsLink'))} ↗</a>
    <a href="/llms.txt" target="_blank" rel="noopener" class="btn btn--ghost btn--sm" style="text-decoration:none;">${escapeHtml(tt('llmsLink'))} ↗</a>
  </div>
  <p class="dash-sub dash-callout dash-callout--note">${tt('defaultsBanner')}</p>
  <div data-api-tokens-list style="margin-top:16px;"></div>
  <div style="margin-top:16px;">
    <button type="button" class="btn btn--ghost btn--sm" data-api-tokens-new>${escapeHtml(tt('createNewToken'))}</button>
  </div>
  <div data-api-tokens-create hidden class="dash-subpanel">
    <form data-api-tokens-create-form>
      <div style="display:grid; grid-template-columns: 1fr; gap:8px;">
        <label style="font-size:13px;">
          <div style="margin-bottom:4px;">${escapeHtml(tt('nameLabel'))}</div>
          <input type="text" name="name" required maxlength="100" placeholder="${escapeHtml(tt('namePlaceholder'))}" class="dash-input" />
        </label>
        <label style="font-size:13px; display:flex; gap:6px; align-items:center; margin-top:4px;">
          <input type="checkbox" name="allow_purchase" />
          <span>${tt('allowPurchaseLabel')}</span>
        </label>
        <div style="display:grid; grid-template-columns: 1fr 1fr; gap:8px;">
          <label style="font-size:13px;">
            <div style="margin-bottom:4px;">${escapeHtml(tt('dailyLimitLabel'))}</div>
            <input type="number" name="daily_limit_usdt" min="1" max="1000" value="50" class="dash-input" />
          </label>
          <label style="font-size:13px;">
            <div style="margin-bottom:4px;">${escapeHtml(tt('monthlyLimitLabel'))}</div>
            <input type="number" name="monthly_limit_usdt" min="1" max="10000" value="500" class="dash-input" />
          </label>
        </div>
      </div>
      <div style="display:flex; gap:8px; margin-top:12px;">
        <button type="submit" class="btn btn--accent btn--sm" data-api-tokens-create-submit>${escapeHtml(tt('createButton'))}</button>
        <button type="button" class="btn btn--ghost btn--sm" data-api-tokens-create-cancel>${escapeHtml(tt('cancelButton'))}</button>
      </div>
      <p class="dash-sub dash-error" data-api-tokens-create-error hidden style="margin-top:8px;"></p>
    </form>
  </div>
  <div data-api-tokens-reveal hidden class="dash-subpanel dash-subpanel--warn">
    <p style="margin:0 0 8px; font-weight:600;">${escapeHtml(tt('saveNowWarning'))}</p>
    <code data-api-tokens-reveal-value class="dash-code-reveal"></code>
    <div style="margin-top:8px; display:flex; gap:8px;">
      <button type="button" class="btn btn--ghost btn--sm" data-api-tokens-copy>${escapeHtml(tt('copyButton'))}</button>
      <button type="button" class="btn btn--ghost btn--sm" data-api-tokens-reveal-close>${escapeHtml(tt('savedItButton'))}</button>
    </div>
  </div>
</section>`;
}

/* ──────────────────────────────────────────────────────────────────────
 *  Handler wiring — call from dashboard/index.ts after settings panel
 *  renders. Idempotent (re-mount safe).
 * ────────────────────────────────────────────────────────────────────── */


export function tokenRowHtml(t: ApiTokenView): string {
  const status = t.revoked_at
    ? `<span class="dash-token__status dash-token__status--off">${escapeHtml(tt('statusRevoked'))} ${escapeHtml(fmtDateTime(t.revoked_at))}</span>`
    : t.expires_at && new Date(t.expires_at).getTime() <= Date.now()
      ? `<span class="dash-token__status dash-token__status--off">${escapeHtml(tt('statusExpired'))}</span>`
      : `<span class="dash-token__status">${escapeHtml(tt('statusActive'))}</span>`;
  const scope = t.allow_purchase
    ? `<span class="dash-badge dash-badge--strong">${escapeHtml(tt('scopePurchase'))}</span>`
    : `<span class="dash-badge">${escapeHtml(tt('scopeReadOnly'))}</span>`;
  const lastUse = t.last_used_at
    ? `${escapeHtml(fmtDateTime(t.last_used_at))} (${escapeHtml(t.last_used_ip ?? '?')})`
    : escapeHtml(tt('neverUsed'));
  const limitsLine = tt('limitsLine')
    .replace('{daily}', String(t.daily_limit_usdt))
    .replace('{monthly}', String(t.monthly_limit_usdt))
    .replace('{spentDay}', t.spent_today_usdt.toFixed(2))
    .replace('{spentMonth}', t.spent_month_usdt.toFixed(2));
  const lastUseLine = tt('lastUseLine').replace('{lastUse}', lastUse);
  return `
<div data-api-token-id="${escapeHtml(t.id)}" class="dash-token">
  <div class="dash-token__head">
    <div class="dash-token__title">
      <strong>${escapeHtml(t.name)}</strong> ${scope}
      <code class="dash-token__prefix">${escapeHtml(t.prefix_hint)}…</code>
    </div>
    ${status}
  </div>
  <div class="dash-sub" style="margin-top:6px;">${escapeHtml(limitsLine)}</div>
  <div class="dash-sub" style="margin-top:2px;">${lastUseLine}</div>
  ${t.revoked_at ? '' : `<div style="margin-top:6px;"><button type="button" class="btn btn--ghost btn--sm" data-api-tokens-revoke="${escapeHtml(t.id)}" style="font-size:12px;">${escapeHtml(tt('revokeButton'))}</button></div>`}
</div>`;
}

async function fetchTokens(): Promise<ApiTokenView[]> {
  const resp = await fetch('/api/me/api-tokens', { credentials: 'include' });
  if (!resp.ok) throw new Error(`status ${resp.status}`);
  const data = await resp.json() as { tokens: ApiTokenView[] };
  return data.tokens;
}

async function renderTokensList(section: HTMLElement): Promise<void> {
  const list = section.querySelector<HTMLElement>('[data-api-tokens-list]');
  if (!list) return;
  list.textContent = tt('loadingTokens');
  try {
    const tokens = await fetchTokens();
    if (tokens.length === 0) {
      list.innerHTML = `<p class="dash-sub">${escapeHtml(tt('noTokensYet'))}</p>`;
      return;
    }
    list.innerHTML = tokens.map(tokenRowHtml).join('');
  } catch (e) {
    list.innerHTML = `<p class="dash-sub dash-error">${escapeHtml(tt('loadFailed'))}: ${escapeHtml(String((e as Error).message))}</p>`;
  }
}

export function wireApiTokensHandlers(root: ParentNode = document): void {
  const section = root.querySelector<HTMLElement>('[data-api-tokens-section]');
  if (!section || section.dataset.wired === '1') return;
  section.dataset.wired = '1';

  void renderTokensList(section);

  const createButton = section.querySelector<HTMLButtonElement>('[data-api-tokens-new]');
  const createPanel = section.querySelector<HTMLElement>('[data-api-tokens-create]');
  const createForm = section.querySelector<HTMLFormElement>('[data-api-tokens-create-form]');
  const cancelButton = section.querySelector<HTMLButtonElement>('[data-api-tokens-create-cancel]');
  const errorBox = section.querySelector<HTMLElement>('[data-api-tokens-create-error]');
  const revealBox = section.querySelector<HTMLElement>('[data-api-tokens-reveal]');
  const revealValue = section.querySelector<HTMLElement>('[data-api-tokens-reveal-value]');
  const copyButton = section.querySelector<HTMLButtonElement>('[data-api-tokens-copy]');
  const closeRevealButton = section.querySelector<HTMLButtonElement>('[data-api-tokens-reveal-close]');

  createButton?.addEventListener('click', () => {
    if (!createPanel) return;
    createPanel.hidden = false;
    createButton.hidden = true;
  });

  cancelButton?.addEventListener('click', () => {
    if (!createPanel || !createButton) return;
    createPanel.hidden = true;
    createButton.hidden = false;
    if (errorBox) errorBox.hidden = true;
  });

  createForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!createForm) return;
    if (errorBox) errorBox.hidden = true;
    const fd = new FormData(createForm);
    const body = {
      name: String(fd.get('name') ?? '').trim(),
      allow_purchase: fd.get('allow_purchase') === 'on',
      daily_limit_usdt: Number(fd.get('daily_limit_usdt') ?? 50),
      monthly_limit_usdt: Number(fd.get('monthly_limit_usdt') ?? 500),
    };
    try {
      const resp = await fetch('/api/me/api-tokens', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json() as { token_plain?: string; error?: string; detail?: string };
      if (!resp.ok || !data.token_plain) {
        const msg = data.detail ?? data.error ?? `HTTP ${resp.status}`;
        throw new Error(msg);
      }
      if (createPanel) createPanel.hidden = true;
      if (createButton) createButton.hidden = false;
      if (revealBox && revealValue) {
        revealValue.textContent = data.token_plain;
        revealBox.hidden = false;
      }
      createForm.reset();
      void renderTokensList(section);
    } catch (err) {
      if (errorBox) {
        errorBox.textContent = `${tt('createFailed')}: ${(err as Error).message}`;
        errorBox.hidden = false;
      }
    }
  });

  copyButton?.addEventListener('click', async () => {
    const val = revealValue?.textContent ?? '';
    try {
      await navigator.clipboard.writeText(val);
      if (copyButton) {
        const original = copyButton.textContent ?? tt('copyButton');
        copyButton.textContent = tt('copiedButton');
        setTimeout(() => { if (copyButton) copyButton.textContent = original; }, 1500);
      }
    } catch { /* ignore */ }
  });

  closeRevealButton?.addEventListener('click', () => {
    if (revealValue) revealValue.textContent = '';
    if (revealBox) revealBox.hidden = true;
  });

  section.addEventListener('click', async (e) => {
    const target = e.target as HTMLElement | null;
    const btn = target?.closest<HTMLButtonElement>('[data-api-tokens-revoke]');
    if (!btn) return;
    const id = btn.getAttribute('data-api-tokens-revoke');
    if (!id) return;
    if (!confirm(tt('revokeConfirm'))) return;
    try {
      const resp = await fetch(`/api/me/api-tokens/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      void renderTokensList(section);
    } catch (err) {
      alert(`${tt('revokeFailed')}: ${(err as Error).message}`);
    }
  });
}
